import { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'
import { useAuth } from '../context/AuthContext'
import { useNavigate, useParams } from 'react-router-dom'
import Navbar from '../components/Navbar'

export default function EditProgramTemplate() {
  const { templateId } = useParams()
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [exercises, setExercises] = useState([])
  const [selectedExercises, setSelectedExercises] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => { fetchAll() }, [])

  async function fetchAll() {
    const [{ data: template }, { data: exerciseData }] = await Promise.all([
      supabase.from('diagnosis_templates').select('*, diagnosis_template_exercises(*, exercises(*))').eq('id', templateId).single(),
      supabase.from('exercises').select('*').eq('clinic_id', profile.clinic_id).order('title')
    ])
    if (template) {
      setName(template.name)
      setDescription(template.description || '')
      setSelectedExercises(template.diagnosis_template_exercises.map(te => ({
        id: te.id,
        exercise_id: te.exercise_id,
        title: te.exercises?.title,
        sets: te.sets || '',
        reps: te.reps || '',
        duration_seconds: te.duration_seconds || '',
        frequency: te.frequency || 'daily',
        num_weeks: te.num_weeks || ''
      })))
    }
    setExercises(exerciseData || [])
    setLoading(false)
  }

  function addExercise(exerciseId) {
    if (!exerciseId || selectedExercises.find(e => e.exercise_id === exerciseId)) return
    const exercise = exercises.find(e => e.id === exerciseId)
    setSelectedExercises(prev => [...prev, {
      exercise_id: exerciseId,
      title: exercise.title,
      sets: '', reps: '', duration_seconds: '',
      frequency: 'daily', num_weeks: ''
    }])
  }

  function updateExercise(index, field, value) {
    setSelectedExercises(prev => prev.map((e, i) => i === index ? { ...e, [field]: value } : e))
  }

  function removeExercise(index) {
    setSelectedExercises(prev => prev.filter((_, i) => i !== index))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setSaving(true)
    setError('')

    const { error: updateError } = await supabase
      .from('diagnosis_templates')
      .update({ name, description })
      .eq('id', templateId)

    if (updateError) { setError(updateError.message); setSaving(false); return }

    await supabase.from('diagnosis_template_exercises').delete().eq('template_id', templateId)

    if (selectedExercises.length > 0) {
      await supabase.from('diagnosis_template_exercises').insert(
        selectedExercises.map((e, i) => ({
          template_id: templateId,
          exercise_id: e.exercise_id,
          sets: e.sets ? parseInt(e.sets) : null,
          reps: e.reps ? parseInt(e.reps) : null,
          duration_seconds: e.duration_seconds ? parseInt(e.duration_seconds) : null,
          frequency: e.frequency,
          num_weeks: e.num_weeks ? parseInt(e.num_weeks) : null,
          sort_order: i
        }))
      )
    }

    navigate('/clinician/diagnoses')
  }

  if (loading) return <><Navbar /><div className="page"><p>Loading...</p></div></>

  return (
    <>
      <Navbar />
      <div className="page">
        <span onClick={() => navigate('/clinician/diagnoses')} className="back-link" style={{ cursor: 'pointer' }}>← Program templates</span>
        <div className="page-header">
          <h1>Edit program template</h1>
        </div>

        {error && <div className="alert-error" style={{ marginBottom: '1.5rem' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="form-group">
              <div className="form-field">
                <label className="form-label">Program name</label>
                <input placeholder="e.g. Rotator cuff tear" value={name} onChange={e => setName(e.target.value)} required />
              </div>
              <div className="form-field">
                <label className="form-label">Description (optional)</label>
                <textarea placeholder="Brief notes about this protocol" value={description} onChange={e => setDescription(e.target.value)} rows={2} style={{ resize: 'vertical' }} />
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem' }}>Exercises</p>
            <div className="form-group">
              <select onChange={e => { addExercise(e.target.value); e.target.value = '' }} defaultValue="">
                <option value="">Add exercise from library...</option>
                {exercises.filter(ex => !selectedExercises.find(s => s.exercise_id === ex.id)).map(ex => (
                  <option key={ex.id} value={ex.id}>{ex.title}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
              {selectedExercises.map((ex, i) => (
                <div key={ex.exercise_id} style={{ padding: '12px', background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', margin: 0 }}>{ex.title}</p>
                    <button type="button" className="danger" onClick={() => removeExercise(i)} style={{ fontSize: 12 }}>Remove</button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
                    <div className="form-field">
                      <label className="form-label" style={{ fontWeight: 400, fontSize: 12 }}>Sets</label>
                      <input type="number" placeholder="3" min="1" value={ex.sets} onChange={e => updateExercise(i, 'sets', e.target.value)} />
                    </div>
                    <div className="form-field">
                      <label className="form-label" style={{ fontWeight: 400, fontSize: 12 }}>Reps</label>
                      <input type="number" placeholder="10" min="1" value={ex.reps} onChange={e => updateExercise(i, 'reps', e.target.value)} />
                    </div>
                    <div className="form-field">
                      <label className="form-label" style={{ fontWeight: 400, fontSize: 12 }}>Duration (sec)</label>
                      <input type="number" placeholder="30" min="1" value={ex.duration_seconds} onChange={e => updateExercise(i, 'duration_seconds', e.target.value)} />
                    </div>
                    <div className="form-field">
                      <label className="form-label" style={{ fontWeight: 400, fontSize: 12 }}>Weeks</label>
                      <input type="number" placeholder="4" min="1" value={ex.num_weeks} onChange={e => updateExercise(i, 'num_weeks', e.target.value)} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button type="submit" disabled={saving} style={{ flex: 1, padding: 14 }}>
              {saving ? 'Saving...' : 'Save changes'}
            </button>
            <button type="button" className="secondary" onClick={() => navigate('/clinician/diagnoses')} style={{ padding: 14 }}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </>
  )
}
